import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Form, Input, Button, Typography, Card, message, Space, Checkbox, Alert } from 'antd';
import { UserOutlined, LockOutlined, BankOutlined, PhoneOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import Logo from './Logo';
import PlacesAutocomplete from './PlacesAutocomplete';
import TermsModal from './TermsModal';
import { sendWebhook, WEBHOOKS } from '../lib/webhooks';

const { Title, Text } = Typography;

interface RegisterFormData {
  email: string;
  password: string;
  confirmPassword: string;
  bedrijfsnaam: string;
  voornaam: string;
  achternaam: string;
  telefoon: string;
  kvk_nummer: string;
  btw_nummer: string;
  adres: string;
  postcode: string;
  stad: string;
  voornaam_financieel: string;
  achternaam_financieel: string;
  telefoon_financieel: string;
  email_financieel: string;
  sameAsContact: boolean;
  acceptTerms: boolean;
}

export default function Register() {
  const [loading, setLoading] = useState(false);
  const [sameAsContact, setSameAsContact] = useState(false);
  const [termsModalVisible, setTermsModalVisible] = useState(false);
  const [addressSelected, setAddressSelected] = useState(false);
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [form] = Form.useForm();

  const handleSubmit = async (values: RegisterFormData) => {
    if (loading) return;

    try {
      setLoading(true);

      // Sign up the user
      const { data: authData, error: authError } = await signUp(values.email, values.password, {
        bedrijfsnaam: values.bedrijfsnaam,
        voornaam: values.voornaam,
        achternaam: values.achternaam,
        telefoon: values.telefoon,
        kvk_nummer: values.kvk_nummer,
        btw_nummer: values.btw_nummer,
        adres: values.adres,
        postcode: values.postcode,
        stad: values.stad,
        land: 'Nederland',
        voornaam_financieel: values.voornaam_financieel,
        achternaam_financieel: values.achternaam_financieel,
        telefoon_financieel: values.telefoon_financieel,
        email_financieel: values.email_financieel,
        acceptedTerms: values.acceptTerms
      });

      if (authError) {
        if (authError.message.includes('already registered')) {
          message.error('Dit e-mailadres is al in gebruik. Ga naar de inlogpagina.');
          navigate('/login');
          return;
        }
        if (authError.message.includes('rate limit')) {
          message.error('Even geduld alstublieft, probeer het over een paar minuten opnieuw.');
          return;
        }
        throw authError;
      }
      
      if (!authData?.user) throw new Error('No user data returned');

      // Save the address to saved_addresses table
      try {
        await supabase
          .from('saved_addresses')
          .insert({
            user_id: authData.user.id,
            name: 'Hoofdadres', // Default name for the first address
            street: values.adres,
            postal_code: values.postcode,
            city: values.stad,
            country: 'Nederland',
            is_default: true // Make this the default address
          });
          
        console.log('Address saved to saved_addresses table');
      } catch (addressError) {
        console.error('Error saving address to saved_addresses:', addressError);
        // Don't fail the registration process due to address saving failure
      }

      // Send webhook for user registration
      try {
        await sendWebhook(WEBHOOKS.USER_REGISTERED, {
          event: 'user.registered',
          user_id: authData.user.id,
          email: values.email,
          company: {
            bedrijfsnaam: values.bedrijfsnaam,
            kvk_nummer: values.kvk_nummer,
            btw_nummer: values.btw_nummer,
            adres: values.adres,
            postcode: values.postcode,
            stad: values.stad,
            land: 'Nederland',
            // Financial contact information
            voornaam_financieel: values.voornaam_financieel,
            achternaam_financieel: values.achternaam_financieel,
            telefoon_financieel: values.telefoon_financieel,
            email_financieel: values.email_financieel
          },
          contact: {
            voornaam: values.voornaam,
            achternaam: values.achternaam,
            telefoon: values.telefoon
          },
          registered_at: new Date().toISOString()
        });
        console.log('User registration webhook sent successfully');
      } catch (webhookError) {
        console.error('Error sending user registration webhook:', webhookError);
        // Don't fail the registration process due to webhook failure
      }

      message.success('Account aangemaakt! Controleer uw e-mail om uw account te activeren.');
      navigate('/login');
    } catch (error: any) {
      console.error('Error during registration:', error);
      
      if (error?.message?.includes('rate limit')) {
        message.error('Even geduld alstublieft, probeer het over een paar minuten opnieuw.');
      } else if (error?.message === 'No user data returned') {
        message.error('Er ging iets mis bij het aanmaken van uw account. Probeer het opnieuw.');
      } else {
        message.error('Registratie mislukt. Probeer het opnieuw.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePlaceSelect = (address: { street: string; postalCode: string; city: string }) => {
    form.setFieldsValue({
      adres: address.street,
      postcode: address.postalCode,
      stad: address.city,
    });
    setAddressSelected(true);
    
    // Trigger validation for the hidden address fields
    form.validateFields(['adres', 'postcode', 'stad']);
  };

  const handleAddressChange = (value: string) => {
    // If the field is cleared or changed, reset the selection state
    if (!value) {
      setAddressSelected(false);
      form.setFieldsValue({
        adres: '',
        postcode: '',
        stad: '',
      });
    }
  };

  const handleSameAsContactChange = (e: any) => {
    const checked = e.target.checked;
    setSameAsContact(checked);
    if (checked) {
      const currentValues = form.getFieldsValue();
      form.setFieldsValue({
        voornaam_financieel: currentValues.voornaam,
        achternaam_financieel: currentValues.achternaam,
        telefoon_financieel: currentValues.telefoon,
        email_financieel: currentValues.email
      });
    } else {
      form.setFieldsValue({
        voornaam_financieel: '',
        achternaam_financieel: '',
        telefoon_financieel: '',
        email_financieel: ''
      });
    }
  };

  const showTermsModal = (e: React.MouseEvent) => {
    e.preventDefault();
    setTermsModalVisible(true);
  };

  const closeTermsModal = () => {
    setTermsModalVisible(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      background: '#001350',
      backgroundImage: 'linear-gradient(135deg, #001350 0%, #002080 100%)',
    }}>
      <Card 
        style={{ 
          width: '100%', 
          maxWidth: 600,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          borderRadius: '8px',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Logo height={48} darkMode={true} />
          <Title level={3} style={{ marginTop: 24, color: '#001350' }}>Account aanmaken</Title>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={false}
        >
          <Title level={4} style={{ color: '#001350', marginBottom: 24 }}>Account Gegevens</Title>
          <Form.Item
            name="email"
            rules={[
              { required: true, message: 'Voer uw e-mailadres in' },
              { type: 'email', message: 'Voer een geldig e-mailadres in' }
            ]}
          >
            <Input 
              prefix={<UserOutlined />}
              placeholder="E-mailadres"
              disabled={loading}
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: 'Voer een wachtwoord in' },
              { min: 6, message: 'Wachtwoord moet minimaal 6 tekens lang zijn' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Wachtwoord"
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Bevestig uw wachtwoord' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('De wachtwoorden komen niet overeen'));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Bevestig wachtwoord"
            />
          </Form.Item>

          <Space style={{ display: 'flex', marginBottom: 24 }}>
            <Form.Item
              name="voornaam"
              rules={[{ required: true, message: 'Voer uw voornaam in' }]}
              style={{ marginBottom: 0, flex: 1 }}
            >
              <Input placeholder="Voornaam" />
            </Form.Item>

            <Form.Item
              name="achternaam"
              rules={[{ required: true, message: 'Voer uw achternaam in' }]}
              style={{ marginBottom: 0, flex: 1 }}
            >
              <Input placeholder="Achternaam" />
            </Form.Item>
          </Space>

          <Form.Item
            name="telefoon"
            rules={[{ required: true, message: 'Voer uw telefoonnummer in' }]}
          >
            <Input
              prefix={<PhoneOutlined />}
              placeholder="Telefoonnummer"
            />
          </Form.Item>

          <Title level={4} style={{ color: '#001350', marginBottom: 24 }}>Bedrijfsgegevens</Title>
          <Form.Item
            name="bedrijfsnaam"
            rules={[{ required: true, message: 'Voer uw bedrijfsnaam in' }]}
          >
            <Input
              prefix={<BankOutlined />}
              placeholder="Bedrijfsnaam"
            />
          </Form.Item>

          <Space style={{ display: 'flex', marginBottom: 24 }}>
            <Form.Item
              name="kvk_nummer"
              rules={[{ required: true, message: 'Voer uw KVK nummer in' }]}
              style={{ marginBottom: 0, flex: 1 }}
            >
              <Input placeholder="KVK Nummer" />
            </Form.Item>

            <Form.Item
              name="btw_nummer"
              rules={[{ required: true, message: 'Voer uw BTW nummer in' }]}
              style={{ marginBottom: 0, flex: 1 }}
            >
              <Input placeholder="BTW Nummer" />
            </Form.Item>
          </Space>

          <Title level={4} style={{ color: '#001350', marginBottom: 24 }}>Financiële contactgegevens</Title>
          
          <Form.Item name="sameAsContact" valuePropName="checked" style={{ marginBottom: 16 }}>
            <Checkbox onChange={handleSameAsContactChange}>
              Dezelfde gegevens als hierboven gebruiken
            </Checkbox>
          </Form.Item>

          <Space style={{ display: 'flex', marginBottom: 24 }}>
            <Form.Item
              name="voornaam_financieel"
              rules={[{ required: true, message: 'Voer de voornaam van uw financieel contactpersoon in' }]}
              style={{ marginBottom: 0, flex: 1 }}
            >
              <Input placeholder="Voornaam" />
            </Form.Item>

            <Form.Item
              name="achternaam_financieel"
              rules={[{ required: true, message: 'Voer de achternaam van uw financieel contactpersoon in' }]}
              style={{ marginBottom: 0, flex: 1 }}
            >
              <Input placeholder="Achternaam" />
            </Form.Item>
          </Space>

          <Form.Item
            name="telefoon_financieel"
            rules={[{ required: true, message: 'Voer het telefoonnummer van uw financieel contactpersoon in' }]}
          >
            <Input
              prefix={<PhoneOutlined />}
              placeholder="Telefoonnummer contactpersoon financiën"
            />
          </Form.Item>

          <Form.Item
            name="email_financieel"
            rules={[
              { required: true, message: 'Voer het e-mailadres van uw financieel contactpersoon in' },
              { type: 'email', message: 'Voer een geldig e-mailadres in' }
            ]}
          >
            <Input 
              prefix={<UserOutlined />}
              placeholder="E-mailadres contactpersoon financiën"
            />
          </Form.Item>

          <Title level={4} style={{ color: '#001350', marginBottom: 24 }}>Adresgegevens</Title>
          
          <Form.Item
            label="Adres"
            style={{ marginBottom: 16 }}
          >
            <PlacesAutocomplete
              onSelect={handlePlaceSelect}
              onAddressChange={handleAddressChange}
              skipAutocompleteOnChange={true}
              placeholder="Typ uw adres en selecteer uit de lijst..."
              status={!addressSelected ? 'warning' : ''}
            />
          </Form.Item>

          {addressSelected && (
            <Alert
              message="Adres geselecteerd"
              description="Uw adres is succesvol geselecteerd en ingevuld."
              type="success"
              icon={<CheckCircleOutlined />}
              style={{ marginBottom: 24 }}
              showIcon
            />
          )}

          <Form.Item
            name="adres"
            style={{ display: 'none' }}
            rules={[{ required: true, message: 'Selecteer een adres uit de suggesties' }]}
          >
            <Input />
          </Form.Item>

          <Space style={{ display: 'flex', marginBottom: 24 }}>
            <Form.Item
              name="postcode"
              label="Postcode"
              rules={[{ required: true, message: 'Selecteer een adres uit de suggesties' }]}
              style={{ marginBottom: 0, flex: 1 }}
            >
              <Input disabled={!addressSelected} />
            </Form.Item>

            <Form.Item
              name="stad"
              label="Stad"
              rules={[{ required: true, message: 'Selecteer een adres uit de suggesties' }]}
              style={{ marginBottom: 0, flex: 1 }}
            >
              <Input disabled={!addressSelected} />
            </Form.Item>
          </Space>

          <Form.Item
            name="acceptTerms"
            valuePropName="checked"
            rules={[
              { 
                validator: (_, value) => 
                  value ? Promise.resolve() : Promise.reject(new Error('U moet akkoord gaan met de algemene voorwaarden'))
              }
            ]}
          >
            <Checkbox>
              Ik ga akkoord met de <button type="button" onClick={showTermsModal} style={{ color: '#FF5d00', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline' }}>algemene voorwaarden</button>
            </Checkbox>
          </Form.Item>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              block
              size="large"
              style={{
                backgroundColor: loading ? '#999' : '#FF5d00',
                borderColor: loading ? '#999' : '#FF5d00',
                height: '45px',
                fontSize: '16px',
                marginTop: '24px',
              }}
              disabled={loading}
            >
              {loading ? 'Account wordt aangemaakt...' : 'Account aanmaken'}
            </Button>
            {!addressSelected && (
              <div style={{ 
                textAlign: 'center', 
                marginTop: '8px', 
                color: '#faad14',
                fontSize: '14px'
              }}>
                ⚠️ Selecteer eerst een adres uit de suggesties om door te gaan
              </div>
            )}
          </Form.Item>
        </Form>

        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Text>
            Al een account?{' '}
            <Link to="/login" style={{ color: '#FF5d00', fontWeight: 600 }}>
              Log hier in
            </Link>
          </Text>
        </div>
      </Card>

      <TermsModal visible={termsModalVisible} onClose={closeTermsModal} />
    </div>
  );
}
